import { useState, useEffect, useRef } from "react";
import {
    Search, Trash2, Eye, FileImage, Loader2, X,
    ChevronLeft, ChevronRight, Download, Printer, Award,
    CheckCircle, AlertCircle, Sparkles, GraduationCap,
    Calendar, Hash, ArrowLeft, Users, CheckSquare,
    Square, BookOpen, ChevronRight as Chevron,
} from "lucide-react";
import axios from "../../config/axios";
import T from "../../components/translate/AutoTranslator";
import signature from "./signature.png";

const str = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "object") return String(val.etp_name ?? val.name ?? val.module_name ?? val.label ?? "");
    return String(val);
};

const AttestationManager = () => {
    const [view, setView] = useState("projects");
    const [projects, setProjects] = useState([]);
    const [attestations, setAttestations] = useState([]);
    const [learners, setLearners] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedLearnerIds, setSelectedLearnerIds] = useState([]);
    const [selectedAttIds, setSelectedAttIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [learnersLoading, setLearnersLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [searchProjects, setSearchProjects] = useState("");
    const [searchLearners, setSearchLearners] = useState("");
    const [searchAtt, setSearchAtt] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [previewAtt, setPreviewAtt] = useState(null);
    const ITEMS = 10;
    const printRef = useRef();

    useEffect(() => { fetchAll(); }, []);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const [projRes, attRes] = await Promise.all([
                axios.get("/cfp/attestation/getProjet"),
                axios.get("/cfp/attestation"),
            ]);
            setProjects(projRes.data || []);
            setAttestations(attRes.data?.attestations || []);
        } catch { toast("error", "Impossible de charger les données"); }
        finally { setLoading(false); }
    };

    const toast = (type, msg) => {
        if (type === "error") { setError(msg); setTimeout(() => setError(""), 4000); }
        else { setSuccess(msg); setTimeout(() => setSuccess(""), 4000); }
    };

    const pRef = (p) => str(p?.project_reference);
    const pModule = (p) => str(p?.module_name);
    const pEtp = (p) => {
        const raw = p?.etp_name;
        if (!raw) return "";
        if (typeof raw === "object") return str(raw.etp_name ?? raw.name ?? raw);
        return String(raw);
    };
    const pDateDebut = (p) => str(p?.dateDebut);
    const pDateFin = (p) => str(p?.dateFin);

    const attCount = (pid) => attestations.filter(a => a.idProjet === pid).length;
    const coverage = (pid, total) => total > 0 ? Math.round((attCount(pid) / total) * 100) : 0;

    const certifiedIds = (pid) =>
        new Set(attestations.filter(a => a.idProjet === pid).map(a => a.idEmploye));

    /* Only returns learners WITHOUT an attestation for this project */
    const eligibleLearners = () => {
        const certs = certifiedIds(selectedProject?.idProjet);
        return learners.filter(l => !certs.has(l.idEmploye));
    };

    const openProject = async (project) => {
        setSelectedProject(project);
        setSelectedLearnerIds([]);
        setSearchLearners("");
        setView("learners");
        try {
            setLearnersLoading(true);
            const res = await axios.get(`/cfp/attestation/${project.idProjet}/getApprenant`);
            setLearners(res.data || []);
        } catch { toast("error", "Impossible de charger les apprenants"); }
        finally { setLearnersLoading(false); }
    };

    const openAttestations = (project) => {
        setSelectedProject(project);
        setSelectedAttIds([]);
        setSearchAtt("");
        setCurrentPage(1);
        setView("attestations");
    };

    const allLearnersSelected = () => {
        const elig = eligibleLearners();
        return elig.length > 0 && elig.every(l => selectedLearnerIds.includes(l.idEmploye));
    };

    const toggleAllLearners = () => {
        if (allLearnersSelected()) setSelectedLearnerIds([]);
        else setSelectedLearnerIds(eligibleLearners().map(l => l.idEmploye));
    };

    const toggleOneLearner = (id) =>
        setSelectedLearnerIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

    const handleGenerate = async () => {
        if (!selectedLearnerIds.length) return;
        try {
            setSubmitting(true);
            await Promise.all(
                selectedLearnerIds.map(lid =>
                    axios.post("/cfp/attestation", { projet: selectedProject.idProjet, apprenant: lid })
                )
            );
            toast("success", `${selectedLearnerIds.length} attestation(s) générée(s) avec succès`);
            setSelectedLearnerIds([]);
            await fetchAll();
        } catch (err) {
            const msg = err.response?.data?.error;
            toast("error", typeof msg === "object" ? Object.values(msg).flat()[0] : msg || "Erreur");
        } finally { setSubmitting(false); }
    };

    /* Single delete */
    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer cette attestation ?")) return;
        try {
            const res = await axios.post(`/cfp/attestation/${id}/delete`);
            if (res.data.success) { toast("success", "Attestation supprimée"); fetchAll(); }
        } catch (err) { toast("error", err.response?.data?.error || "Erreur"); }
    };

    /* Bulk delete selected attestations */
    const handleDeleteSelected = async () => {
        if (!selectedAttIds.length) return;
        if (!window.confirm(`Supprimer ${selectedAttIds.length} attestation(s) sélectionnée(s) ?`)) return;
        try {
            setDeleting(true);
            await Promise.all(selectedAttIds.map(id => axios.post(`/cfp/attestation/${id}/delete`)));
            toast("success", `${selectedAttIds.length} attestation(s) supprimée(s)`);
            setSelectedAttIds([]);
            await fetchAll();
        } catch (err) { toast("error", err.response?.data?.error || "Erreur lors de la suppression"); }
        finally { setDeleting(false); }
    };

    const toggleOneAtt = (id) =>
        setSelectedAttIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

    const initials = (n, f) => `${str(n).charAt(0)}${str(f).charAt(0)}`.toUpperCase();

    /* ═══ FILTERED DATA ═══ */
    const filteredProjects = projects.filter(p =>
        `${pRef(p)} ${pModule(p)} ${pEtp(p)}`.toLowerCase().includes(searchProjects.toLowerCase())
    );

    /* Learners view: only show eligible (not yet certified) */
    const filteredEligible = eligibleLearners().filter(l =>
        `${str(l.emp_name)} ${str(l.emp_firstname)}`.toLowerCase().includes(searchLearners.toLowerCase())
    );

    const projectAtts = selectedProject
        ? attestations.filter(a => a.idProjet === selectedProject.idProjet)
        : [];
    const filteredAtts = projectAtts.filter(a =>
        `${str(a.name)} ${str(a.firstName)} ${str(a.number_attestation)}`.toLowerCase().includes(searchAtt.toLowerCase())
    );
    const paginated = filteredAtts.slice((currentPage - 1) * ITEMS, currentPage * ITEMS);
    const totalPages = Math.ceil(filteredAtts.length / ITEMS);
    const allAttsSelected = paginated.length > 0 && paginated.every(a => selectedAttIds.includes(a.idAttestation));

    const toggleAllAtts = () => {
        if (allAttsSelected) setSelectedAttIds(p => p.filter(id => !paginated.find(a => a.idAttestation === id)));
        else setSelectedAttIds(p => [...new Set([...p, ...paginated.map(a => a.idAttestation)])]);
    };

    /* ═══ CSS ═══ */
    const css = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
    @keyframes slideIn{from{transform:translateX(110%);opacity:0}to{transform:translateX(0);opacity:1}}
    @keyframes fadeUp{from{transform:translateY(10px);opacity:0}to{transform:translateY(0);opacity:1}}
    @keyframes shimmer{0%{opacity:.6}50%{opacity:1}100%{opacity:.6}}
    .fade-up{animation:fadeUp .22s cubic-bezier(.16,1,.3,1) both}
    .proj-card{transition:all .18s cubic-bezier(.16,1,.3,1);cursor:pointer}
    .proj-card:hover{border-color:#a5b4fc!important;box-shadow:0 6px 24px rgba(99,102,241,.13)!important;transform:translateY(-2px)}
    .tr-row{transition:background .1s}
    .tr-row:hover{background:#fafbff!important}
    .tr-row .row-acts{opacity:0;transition:opacity .15s}
    .tr-row:hover .row-acts{opacity:1}
    .ibtn{transition:all .12s;border:none;cursor:pointer}
    .ibtn:hover{transform:scale(1.1)}
    input:focus,button:focus{outline:none}
    input:focus{border-color:#818cf8!important;box-shadow:0 0 0 3px rgba(129,140,248,.15)!important}
    .toast-anim{animation:slideIn .28s cubic-bezier(.16,1,.3,1)}
    .chk-row{transition:background .1s}
    .chk-row:hover{background:#f5f3ff!important}
    @media print{body *{visibility:hidden}#print-area,#print-area *{visibility:visible}#print-area{position:fixed;top:0;left:0;width:100%}}
  `;

    /* ═══ SUB-COMPONENTS ═══ */

    const Chip = ({ children, color, bg, border }) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", background: bg, color, border: `1px solid ${border || bg}`, borderRadius: 99, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", letterSpacing: ".02em" }}>
            {children}
        </span>
    );

    const Bar = ({ pct }) => (
        <div style={{ height: 3, background: "#f1f5f9", borderRadius: 99, flex: 1, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: pct >= 100 ? "#10b981" : "linear-gradient(90deg,#818cf8,#a78bfa)", borderRadius: 99, transition: "width .5s ease" }} />
        </div>
    );

    const SBox = ({ value, onChange, placeholder, style = {} }) => (
        <div style={{ position: "relative", ...style }}>
            <Search style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "#a0aec0", pointerEvents: "none" }} />
            <input value={value} onChange={onChange} placeholder={placeholder}
                style={{ width: "100%", padding: "9px 12px 9px 34px", border: "1.5px solid #e8edf5", borderRadius: 9, fontSize: 13, color: "#1e293b", background: "#fff", fontFamily: "inherit", transition: "border-color .15s,box-shadow .15s" }} />
        </div>
    );

    const Btn = ({ onClick, disabled, variant = "primary", icon, children, small }) => {
        const styles = {
            primary: { background: "linear-gradient(135deg,#6366f1 0%,#818cf8 100%)", color: "#fff", border: "none", boxShadow: disabled ? "none" : "0 2px 12px rgba(99,102,241,.28)" },
            danger: { background: "#fff", color: "#ef4444", border: "1.5px solid #fecaca" },
            ghost: { background: "#fff", color: "#374151", border: "1.5px solid #e8edf5" },
        };
        return (
            <button onClick={onClick} disabled={disabled}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: small ? "6px 12px" : "8px 16px", borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", whiteSpace: "nowrap", opacity: disabled ? .55 : 1, fontFamily: "inherit", transition: "all .15s", ...styles[variant] }}>
                {icon}{children}
            </button>
        );
    };

    const Breadcrumb = () => (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#94a3b8", flexWrap: "wrap", fontWeight: 500 }}>
            <span onClick={() => setView("projects")} style={{ color: view === "projects" ? "#1e293b" : "#6366f1", cursor: view !== "projects" ? "pointer" : "default", fontWeight: 600 }}>Projets</span>
            {(view === "learners" || view === "attestations") && (<>
                <Chevron style={{ width: 11, height: 11, color: "#d1d5db" }} />
                <span onClick={() => view === "attestations" && setView("learners")} style={{ color: view === "learners" ? "#1e293b" : "#6366f1", cursor: view === "attestations" ? "pointer" : "default", fontWeight: 600, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {pModule(selectedProject) || pRef(selectedProject)}
                </span>
            </>)}
            {view === "attestations" && (<>
                <Chevron style={{ width: 11, height: 11, color: "#d1d5db" }} />
                <span style={{ color: "#1e293b", fontWeight: 600 }}>Attestations</span>
            </>)}
        </div>
    );

    const Modal = ({ isOpen, onClose, title, children }) => {
        if (!isOpen) return null;
        return (
            <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.6)", backdropFilter: "blur(4px)" }} onClick={onClose} />
                <div style={{ position: "relative", background: "#fff", borderRadius: 16, boxShadow: "0 32px 64px rgba(0,0,0,.24)", width: "100%", maxWidth: 880, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 24px", borderBottom: "1px solid #f1f5f9" }}>
                        <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>{title}</h3>
                        <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: "1.5px solid #e8edf5", background: "#f8fafc", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" }}>
                            <X style={{ width: 14, height: 14 }} />
                        </button>
                    </div>
                    <div style={{ overflowY: "auto", padding: 24 }}>{children}</div>
                </div>
            </div>
        );
    };

    const AttestationTemplate = ({ data }) => {
        if (!data) return null;
        const nomComplet = `${str(data.name)} ${str(data.firstName)}`.trim();
        const moduleName = str(data.moduleName) || "Formation";
        const numberAttestation = str(data.number_attestation) || "N/A";
        const dd = str(data.dateDebut); const df = str(data.dateFin);
        const periode = dd && df && dd !== "N/A" ? `${dd} – ${df}` : dd && dd !== "N/A" ? dd : "«Période»";
        const today = new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
        return (
            <div ref={printRef} style={{ fontFamily: "Calibri,'Segoe UI',sans-serif", fontSize: "11pt", background: "#fff", padding: "36px 52px 30px", maxWidth: "760px", margin: "0 auto" }}>
                <p style={{ fontWeight: "bold", textAlign: "center", color: "#0B1D3A", fontSize: "18pt", letterSpacing: ".14em", margin: "0 0 2px" }}>NUMERIKA</p>
                <p style={{ textAlign: "center", color: "#8A8A8A", fontSize: "11pt", letterSpacing: ".04em", margin: "0 0 8px" }}>EXCEL · POWER BI · INTELLIGENCE ARTIFICIELLE</p>
                <hr style={{ border: "none", borderBottom: "1px solid #B8943E", width: "60%", margin: "0 auto 12px" }} />
                <p style={{ fontWeight: "bold", textAlign: "center", color: "#0B1D3A", fontSize: "13pt", letterSpacing: ".1em", margin: "6px 0 4px" }}>ATTESTATION DE FORMATION PROFESSIONNELLE</p>
                <p style={{ textAlign: "center", color: "#8A8A8A", fontSize: "11pt", margin: "0 0 10px" }}>PROFESSIONAL TRAINING CERTIFICATE</p>
                <p style={{ textAlign: "center", color: "#8A8A8A", fontSize: "11pt", letterSpacing: ".05em", margin: "0 0 8px" }}>CERTIFIE QUE · THIS CERTIFIES THAT</p>
                <p style={{ fontWeight: "bold", textAlign: "center", fontFamily: "Garamond,'Times New Roman',serif", color: "#0B1D3A", fontSize: "24pt", margin: "0 0 4px" }}>{nomComplet}</p>
                <hr style={{ border: "none", borderBottom: "1px solid #B8943E", width: "60%", margin: "0 auto 12px" }} />
                <p style={{ textAlign: "center", color: "#5A5A5A", fontSize: "11pt", margin: "0 0 2px" }}>a suivi avec succès la formation professionnelle en</p>
                <p style={{ fontStyle: "italic", textAlign: "center", color: "#8A8A8A", fontSize: "11pt", margin: "0 0 10px" }}>has successfully completed the professional training in</p>
                <hr style={{ border: "none", borderBottom: "1px solid #E8E8E8", margin: "0 0 6px" }} />
                <p style={{ fontWeight: "bold", textAlign: "center", color: "#0B1D3A", fontSize: "12pt", letterSpacing: ".055em", margin: "4px 0 8px" }}>{moduleName}</p>
                <hr style={{ border: "none", borderBottom: "1px solid #E8E8E8", margin: "0 0 10px" }} />
                <p style={{ fontStyle: "italic", textAlign: "center", color: "#8A8A8A", fontSize: "11pt", margin: "0 0 14px", padding: "0 40px" }}>
                    démontrant des compétences pratiques, une pensée analytique et une maîtrise professionnelle conformes aux standards du métier.
                </p>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11pt" }}>
                    <tbody>
                        <tr>
                            <td style={{ padding: "2px 6px", width: "50%" }}><span style={{ color: "#8A8A8A" }}>DURÉE / DURATION &nbsp;</span><span style={{ fontWeight: "bold", color: "#2D2D2D" }}>12 heures / hours</span></td>
                            <td style={{ padding: "2px 6px" }}><span style={{ color: "#8A8A8A" }}>NIVEAU / LEVEL &nbsp;</span><span style={{ fontWeight: "bold", color: "#2D2D2D" }}>Fondamentaux</span></td>
                        </tr>
                        <tr>
                            <td style={{ padding: "2px 6px" }}><span style={{ color: "#8A8A8A" }}>PÉRIODE / PERIOD &nbsp;</span><span style={{ fontWeight: "bold", color: "#2D2D2D" }}>{periode}</span></td>
                            <td style={{ padding: "2px 6px" }}><span style={{ color: "#8A8A8A" }}>MODALITÉ / MODE &nbsp;</span><span style={{ fontWeight: "bold", color: "#2D2D2D" }}>«Modalité»</span></td>
                        </tr>
                    </tbody>
                </table>
                <hr style={{ border: "none", borderBottom: "1px solid #E8E8E8", margin: "8px 0" }} />
                <p style={{ textAlign: "center", color: "#B8943E", fontSize: "11pt", letterSpacing: ".04em", margin: "10px 0 6px" }}>COMPÉTENCES CLÉS VALIDÉES · KEY COMPETENCIES VALIDATED</p>
                <p style={{ textAlign: "center", color: "#2E5C8A", fontSize: "11pt", lineHeight: "1.6", margin: "0 0 10px", padding: "0 40px" }}>
                    Importation des données — Data Import &nbsp;·&nbsp; Transformations de base sur l'éditeur Query — Basic Transformations in the Query Editor
                </p>
                <p style={{ fontWeight: "bold", textAlign: "center", color: "#B8943E", fontSize: "11pt", letterSpacing: ".08em", margin: "6px 0 2px" }}>— VALIDÉ AVEC DISTINCTION —</p>
                <p style={{ fontStyle: "italic", textAlign: "center", color: "#B8943E", fontSize: "11pt", margin: "0 0 14px" }}>Passed with Distinction</p>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11pt" }}>
                    <tbody>
                        <tr>
                            <td style={{ width: "38%", padding: 6, textAlign: "center", verticalAlign: "bottom" }}>
                                <img src={signature} alt="Signature" style={{ height: 26, display: "block", margin: "0 auto 4px", objectFit: "contain" }} />
                                <p style={{ fontWeight: "bold", color: "#2D2D2D", margin: "0 0 2px" }}>RAVELOSON LEVY</p>
                                <p style={{ color: "#8A8A8A", margin: 0 }}>Directeur · Fondateur / Director · Founder</p>
                            </td>
                            <td style={{ width: "32%", padding: 6, textAlign: "center", verticalAlign: "bottom" }}>
                                <p style={{ color: "#5A5A5A", margin: "0 0 2px" }}>N° {numberAttestation}</p>
                                <p style={{ color: "#8A8A8A", margin: 0 }}>Délivré le / Issued on</p>
                                <p style={{ color: "#8A8A8A", margin: 0 }}>{today}</p>
                                <p style={{ color: "#8A8A8A", margin: 0 }}>Antananarivo, Madagascar</p>
                            </td>
                            <td style={{ width: "30%", padding: 6, textAlign: "center", verticalAlign: "bottom" }}>
                                <p style={{ color: "#8A8A8A", margin: 0 }}>[QR Code]</p>
                                <p style={{ color: "#8A8A8A", margin: 0 }}>Vérifier sur / Verify at</p>
                                <p style={{ color: "#8A8A8A", margin: 0 }}>numerika.center/verify</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <hr style={{ border: "none", borderBottom: "1px solid #E8E8E8", margin: "10px 0 8px" }} />
                <p style={{ textAlign: "center", color: "#8A8A8A", fontSize: "11pt", margin: "0 0 2px" }}>NUMERIKA · Antananarivo, Madagascar · numerika.center</p>
                <p style={{ fontStyle: "italic", textAlign: "center", color: "#B0B0B0", fontSize: "11pt", margin: "0 0 2px" }}>Forming Africa's next generation of data professionals</p>
                <p style={{ textAlign: "center", color: "#B8943E", fontSize: "11pt", margin: 0 }}>— 2026 EDITION —</p>
            </div>
        );
    };

    /* ═══ LOADING ═══ */
    if (loading) return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 360, gap: 14, fontFamily: "DM Sans,sans-serif" }}>
            <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
            <div style={{ width: 52, height: 52, background: "linear-gradient(135deg,#eef2ff,#e0e7ff)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Loader2 style={{ width: 24, height: 24, color: "#6366f1", animation: "spin 1s linear infinite" }} />
            </div>
            <span style={{ fontSize: 14, color: "#94a3b8", fontWeight: 500 }}>Chargement des données…</span>
        </div>
    );

    /* ═══════════════════════ VIEW: PROJECTS ═══════════════════════ */
    const ProjectsView = () => (
        <div className="fade-up">
            {/* KPIs */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 28 }}>
                {[
                    { icon: <BookOpen style={{ width: 18, height: 18 }} />, label: "Projets actifs", value: projects.length, color: "#6366f1", bg: "linear-gradient(135deg,#eef2ff,#e0e7ff)", border: "#c7d2fe" },
                    { icon: <Award style={{ width: 18, height: 18 }} />, label: "Attestations émises", value: attestations.length, color: "#d97706", bg: "linear-gradient(135deg,#fffbeb,#fef3c7)", border: "#fde68a" },
                    { icon: <GraduationCap style={{ width: 18, height: 18 }} />, label: "Apprenants certifiés", value: new Set(attestations.map(a => a.idEmploye)).size, color: "#059669", bg: "linear-gradient(135deg,#ecfdf5,#d1fae5)", border: "#a7f3d0" },
                ].map((k, i) => (
                    <div key={i} style={{ background: "#fff", border: `1.5px solid ${k.border}`, borderRadius: 14, padding: "18px 20px", display: "flex", alignItems: "center", gap: 14, boxShadow: "0 2px 8px rgba(0,0,0,.04)" }}>
                        <div style={{ width: 46, height: 46, background: k.bg, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: k.color, flexShrink: 0, border: `1px solid ${k.border}` }}>{k.icon}</div>
                        <div>
                            <div style={{ fontSize: 26, fontWeight: 800, color: "#0f172a", lineHeight: 1, letterSpacing: "-.02em" }}>{k.value}</div>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, fontWeight: 500 }}>{k.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Toolbar */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8" }}>{filteredProjects.length} projet(s)</span>
                <SBox value={searchProjects} onChange={e => setSearchProjects(e.target.value)} placeholder="Rechercher un projet…" style={{ width: 270 }} />
            </div>

            {/* Project cards */}
            {filteredProjects.length === 0 ? (
                <div style={{ textAlign: "center", padding: "56px 0", color: "#94a3b8", fontSize: 14 }}>Aucun projet trouvé</div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {filteredProjects.map(p => {
                        const cnt = attCount(p.idProjet);
                        const total = Number(p.learner_count ?? p.nbApprenants ?? 0);
                        const pct = coverage(p.idProjet, total);
                        const done = total > 0 && pct >= 100;
                        return (
                            <div key={p.idProjet} className="proj-card"
                                style={{ background: "#fff", border: "1.5px solid #eef2f7", borderRadius: 13, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16, boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>

                                <div style={{ width: 42, height: 42, background: done ? "linear-gradient(135deg,#ecfdf5,#d1fae5)" : "linear-gradient(135deg,#eef2ff,#e0e7ff)", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: done ? "1px solid #a7f3d0" : "1px solid #c7d2fe" }}>
                                    {done ? <CheckCircle style={{ width: 19, height: 19, color: "#10b981" }} /> : <BookOpen style={{ width: 19, height: 19, color: "#6366f1" }} />}
                                </div>

                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                                        <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}>
                                            {pModule(p) || pRef(p)}
                                        </span>
                                        <span style={{ fontSize: 11, color: "#94a3b8", background: "#f8fafc", border: "1px solid #e8edf5", borderRadius: 6, padding: "1px 7px", whiteSpace: "nowrap", fontFamily: "DM Mono,monospace" }}>
                                            {pRef(p)}
                                        </span>
                                        {pEtp(p) && <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>· {pEtp(p)}</span>}
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <Bar pct={total > 0 ? pct : 0} />
                                        <span style={{ fontSize: 12, fontWeight: 700, color: done ? "#10b981" : "#6366f1", whiteSpace: "nowrap", minWidth: 108, textAlign: "right" }}>
                                            {total > 0 ? `${pct}% · ${cnt}/${total}` : `${cnt} att.`}
                                        </span>
                                    </div>
                                </div>

                                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                                    <Btn onClick={() => openAttestations(p)} variant="ghost" icon={<Eye style={{ width: 13, height: 13 }} />}>
                                        Voir ({cnt})
                                    </Btn>
                                    <Btn onClick={() => openProject(p)} icon={<Sparkles style={{ width: 13, height: 13 }} />}>
                                        Générer
                                    </Btn>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    /* ═══════════════════════ VIEW: LEARNERS ═══════════════════════ */
    /* Only eligible (not yet certified) learners are shown */
    const LearnersView = () => (
        <div className="fade-up">
            {/* Banner */}
            <div style={{ background: "#fff", border: "1.5px solid #eef2f7", borderRadius: 13, padding: "16px 20px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 42, height: 42, background: "linear-gradient(135deg,#eef2ff,#e0e7ff)", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #c7d2fe" }}>
                        <BookOpen style={{ width: 18, height: 18, color: "#6366f1" }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{pModule(selectedProject)}</div>
                        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{pRef(selectedProject)}{pEtp(selectedProject) ? ` · ${pEtp(selectedProject)}` : ""}</div>
                    </div>
                </div>
                <Chip color="#059669" bg="#ecfdf5" border="#a7f3d0">
                    <CheckCircle style={{ width: 10, height: 10 }} /> {certifiedIds(selectedProject?.idProjet).size} certifié(s)
                </Chip>
            </div>

            {/* Toolbar */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                <SBox value={searchLearners} onChange={e => setSearchLearners(e.target.value)} placeholder="Rechercher…" style={{ flex: 1, minWidth: 200 }} />
                {filteredEligible.length > 0 && (
                    <button onClick={toggleAllLearners}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "1.5px solid #e8edf5", borderRadius: 9, background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit" }}>
                        {allLearnersSelected()
                            ? <CheckSquare style={{ width: 14, height: 14, color: "#6366f1" }} />
                            : <Square style={{ width: 14, height: 14, color: "#94a3b8" }} />}
                        {allLearnersSelected() ? "Tout désélectionner" : "Tout sélectionner"}
                    </button>
                )}
                <Btn onClick={handleGenerate} disabled={selectedLearnerIds.length === 0 || submitting}
                    icon={submitting ? <Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> : <Sparkles style={{ width: 13, height: 13 }} />}>
                    {submitting ? "Génération…" : selectedLearnerIds.length > 0 ? `Générer (${selectedLearnerIds.length})` : "Générer"}
                </Btn>
            </div>

            {/* Table — only eligible learners, no "en attente" badge */}
            {learnersLoading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 0", gap: 10, color: "#94a3b8" }}>
                    <Loader2 style={{ width: 20, height: 20, animation: "spin 1s linear infinite" }} />
                    <span style={{ fontSize: 14 }}>Chargement…</span>
                </div>
            ) : (
                <div style={{ background: "#fff", border: "1.5px solid #eef2f7", borderRadius: 13, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
                    {filteredEligible.length === 0 ? (
                        <div style={{ padding: "64px 24px", textAlign: "center" }}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                                <div style={{ width: 52, height: 52, background: "linear-gradient(135deg,#ecfdf5,#d1fae5)", borderRadius: 13, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #a7f3d0" }}>
                                    <CheckCircle style={{ width: 22, height: 22, color: "#10b981" }} />
                                </div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Tous les apprenants sont certifiés</div>
                                <div style={{ fontSize: 13, color: "#94a3b8" }}>Il n'y a plus d'apprenant éligible pour ce projet</div>
                            </div>
                        </div>
                    ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: "#f8fafc", borderBottom: "1.5px solid #eef2f7" }}>
                                    <th style={{ width: 48, padding: "11px 16px" }}>
                                        <button onClick={toggleAllLearners} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}>
                                            {allLearnersSelected()
                                                ? <CheckSquare style={{ width: 15, height: 15, color: "#6366f1" }} />
                                                : <Square style={{ width: 15, height: 15, color: "#cbd5e1" }} />}
                                        </button>
                                    </th>
                                    <th style={{ padding: "11px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".07em" }}>Apprenant</th>
                                    <th style={{ padding: "11px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".07em" }}>Sélection</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEligible.map(l => {
                                    const isSel = selectedLearnerIds.includes(l.idEmploye);
                                    const nameStr = `${str(l.emp_name)} ${str(l.emp_firstname)}`.trim();
                                    return (
                                        <tr key={l.idEmploye} className="chk-row"
                                            onClick={() => toggleOneLearner(l.idEmploye)}
                                            style={{ borderTop: "1px solid #f8fafc", cursor: "pointer", background: isSel ? "#f5f3ff" : "#fff" }}>
                                            <td style={{ padding: "12px 16px", textAlign: "center" }}>
                                                {isSel
                                                    ? <CheckSquare style={{ width: 16, height: 16, color: "#6366f1" }} />
                                                    : <Square style={{ width: 16, height: 16, color: "#cbd5e1" }} />}
                                            </td>
                                            <td style={{ padding: "12px 16px" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <div style={{ width: 34, height: 34, background: isSel ? "linear-gradient(135deg,#ede9fe,#ddd6fe)" : "linear-gradient(135deg,#eef2ff,#e0e7ff)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#6366f1", flexShrink: 0, border: isSel ? "1px solid #c4b5fd" : "1px solid #c7d2fe" }}>
                                                        {initials(l.emp_name, l.emp_firstname)}
                                                    </div>
                                                    <span style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{nameStr}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: "12px 16px" }}>
                                                {isSel
                                                    ? <Chip color="#6366f1" bg="#eef2ff" border="#c7d2fe">Sélectionné</Chip>
                                                    : <span style={{ fontSize: 12, color: "#cbd5e1" }}>—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );

    /* ═══════════════════════ VIEW: ATTESTATIONS LIST ═══════════════════════ */
    const AttestationsView = () => (
        <div className="fade-up">
            {/* Banner */}
            <div style={{ background: "#fff", border: "1.5px solid #eef2f7", borderRadius: 13, padding: "16px 20px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 42, height: 42, background: "linear-gradient(135deg,#fffbeb,#fef3c7)", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fde68a" }}>
                        <Award style={{ width: 18, height: 18, color: "#d97706" }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{pModule(selectedProject)}</div>
                        <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{projectAtts.length} attestation(s) émise(s)</div>
                    </div>
                </div>
                <Btn onClick={() => openProject(selectedProject)} icon={<Sparkles style={{ width: 13, height: 13 }} />}>
                    Générer des attestations
                </Btn>
            </div>

            {/* Toolbar with search + bulk delete */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                <SBox value={searchAtt} onChange={e => { setSearchAtt(e.target.value); setCurrentPage(1); }} placeholder="Rechercher…" style={{ flex: 1, minWidth: 200 }} />
                {selectedAttIds.length > 0 && (
                    <Btn onClick={handleDeleteSelected} disabled={deleting} variant="danger"
                        icon={deleting ? <Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> : <Trash2 style={{ width: 13, height: 13 }} />}>
                        {deleting ? "Suppression…" : `Supprimer (${selectedAttIds.length})`}
                    </Btn>
                )}
            </div>

            {/* Table */}
            <div style={{ background: "#fff", border: "1.5px solid #eef2f7", borderRadius: 13, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
                {paginated.length === 0 ? (
                    <div style={{ padding: "64px 24px", textAlign: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: "#94a3b8" }}>
                            <div style={{ width: 52, height: 52, background: "#f1f5f9", borderRadius: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <FileImage style={{ width: 22, height: 22 }} />
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>Aucune attestation émise</div>
                            <div style={{ fontSize: 13 }}>Ce projet n'a pas encore d'attestations</div>
                            <Btn onClick={() => openProject(selectedProject)} icon={<Sparkles style={{ width: 13, height: 13 }} />}>
                                Générer maintenant
                            </Btn>
                        </div>
                    </div>
                ) : (
                    <>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: "#f8fafc", borderBottom: "1.5px solid #eef2f7" }}>
                                    <th style={{ width: 48, padding: "11px 20px" }}>
                                        <button onClick={toggleAllAtts} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}>
                                            {allAttsSelected
                                                ? <CheckSquare style={{ width: 15, height: 15, color: "#6366f1" }} />
                                                : <Square style={{ width: 15, height: 15, color: "#cbd5e1" }} />}
                                        </button>
                                    </th>
                                    {["Apprenant", "N° Attestation", "Période", "Actions"].map((h, i) => (
                                        <th key={h} style={{ padding: "11px 20px", textAlign: i === 3 ? "right" : "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".07em", whiteSpace: "nowrap" }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map(att => {
                                    const nameStr = `${str(att.name)} ${str(att.firstName)}`.trim();
                                    const numStr = str(att.number_attestation);
                                    const dd = str(att.dateDebut); const df = str(att.dateFin);
                                    const isSel = selectedAttIds.includes(att.idAttestation);
                                    return (
                                        <tr key={att.idAttestation} className="tr-row"
                                            style={{ borderTop: "1px solid #f8fafc", background: isSel ? "#f5f3ff" : "#fff" }}>
                                            <td style={{ padding: "12px 20px" }}>
                                                <button onClick={() => toggleOneAtt(att.idAttestation)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}>
                                                    {isSel
                                                        ? <CheckSquare style={{ width: 15, height: 15, color: "#6366f1" }} />
                                                        : <Square style={{ width: 15, height: 15, color: "#cbd5e1" }} />}
                                                </button>
                                            </td>
                                            <td style={{ padding: "12px 20px", whiteSpace: "nowrap" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <div style={{ width: 34, height: 34, background: "linear-gradient(135deg,#eef2ff,#e0e7ff)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#6366f1", flexShrink: 0, border: "1px solid #c7d2fe" }}>
                                                        {initials(att.name, att.firstName)}
                                                    </div>
                                                    <span style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{nameStr}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: "12px 20px" }}>
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", background: "#f5f3ff", color: "#6366f1", borderRadius: 7, fontSize: 12, fontWeight: 700, fontFamily: "DM Mono,monospace", border: "1px solid #ddd6fe" }}>
                                                    <Hash style={{ width: 10, height: 10 }} />{numStr}
                                                </span>
                                            </td>
                                            <td style={{ padding: "12px 20px" }}>
                                                {dd
                                                    ? <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                                        <Calendar style={{ width: 12, height: 12, color: "#94a3b8", flexShrink: 0 }} />
                                                        <span style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{dd}{df ? ` → ${df}` : ""}</span>
                                                    </div>
                                                    : <span style={{ fontSize: 13, color: "#94a3b8" }}>—</span>}
                                            </td>
                                            <td style={{ padding: "12px 20px", textAlign: "right" }}>
                                                <div className="row-acts" style={{ display: "inline-flex", gap: 6 }}>
                                                    <button onClick={() => setPreviewAtt(att)} className="ibtn"
                                                        style={{ width: 33, height: 33, border: "1.5px solid #e0e7ff", borderRadius: 9, background: "#f5f3ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#6366f1" }}>
                                                        <Eye style={{ width: 14, height: 14 }} />
                                                    </button>
                                                    <button onClick={() => handleDelete(att.idAttestation)} className="ibtn"
                                                        style={{ width: 33, height: 33, border: "1.5px solid #fecaca", borderRadius: 9, background: "#fff5f5", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444" }}>
                                                        <Trash2 style={{ width: 14, height: 14 }} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid #f1f5f9" }}>
                                <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong></span>
                                <div style={{ display: "flex", gap: 6 }}>
                                    <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}
                                        style={{ width: 33, height: 33, border: "1.5px solid #e8edf5", borderRadius: 9, background: currentPage === 1 ? "#f8fafc" : "#fff", cursor: currentPage === 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: currentPage === 1 ? "#cbd5e1" : "#374151" }}>
                                        <ChevronLeft style={{ width: 14, height: 14 }} />
                                    </button>
                                    <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
                                        style={{ width: 33, height: 33, border: "1.5px solid #e8edf5", borderRadius: 9, background: currentPage === totalPages ? "#f8fafc" : "#fff", cursor: currentPage === totalPages ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: currentPage === totalPages ? "#cbd5e1" : "#374151" }}>
                                        <ChevronRight style={{ width: 14, height: 14 }} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );

    /* ═══════════════════════ ROOT ═══════════════════════ */
    return (
        <div style={{ padding: "28px 32px", maxWidth: 1060, margin: "0 auto", fontFamily: "'DM Sans',-apple-system,sans-serif" }}>
            <style>{css}</style>

            {/* Toasts */}
            {(error || success) && (
                <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8 }}>
                    {error && (
                        <div className="toast-anim" style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 17px", background: "#fff", border: "1px solid #fecaca", borderLeft: "4px solid #ef4444", borderRadius: 11, boxShadow: "0 8px 28px rgba(0,0,0,.1)", minWidth: 290 }}>
                            <AlertCircle style={{ width: 16, height: 16, color: "#ef4444", flexShrink: 0 }} />
                            <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 500 }}>{error}</span>
                        </div>
                    )}
                    {success && (
                        <div className="toast-anim" style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 17px", background: "#fff", border: "1px solid #bbf7d0", borderLeft: "4px solid #10b981", borderRadius: 11, boxShadow: "0 8px 28px rgba(0,0,0,.1)", minWidth: 290 }}>
                            <CheckCircle style={{ width: 16, height: 16, color: "#10b981", flexShrink: 0 }} />
                            <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 500 }}>{success}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Header */}
            <div style={{ marginBottom: 26 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    {view !== "projects" && (
                        <button onClick={() => setView("projects")}
                            style={{ width: 32, height: 32, borderRadius: 9, border: "1.5px solid #e8edf5", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6366f1", flexShrink: 0 }}>
                            <ArrowLeft style={{ width: 14, height: 14 }} />
                        </button>
                    )}
                    <div style={{ width: 36, height: 36, background: "linear-gradient(135deg,#6366f1,#818cf8)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 4px 12px rgba(99,102,241,.3)" }}>
                        <Award style={{ width: 17, height: 17, color: "#fff" }} />
                    </div>
                    <h1 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", letterSpacing: "-.03em" }}>
                        <T>Gestion des Attestations</T>
                    </h1>
                </div>
                <div style={{ paddingLeft: view !== "projects" ? 46 : 0 }}>
                    <Breadcrumb />
                </div>
            </div>

            {view === "projects" && <ProjectsView />}
            {view === "learners" && <LearnersView />}
            {view === "attestations" && <AttestationsView />}

            {/* Preview modal */}
            <Modal isOpen={!!previewAtt} onClose={() => setPreviewAtt(null)} title="Aperçu de l'attestation">
                {previewAtt && (
                    <>
                        <div id="print-area" style={{ border: "1px solid #e8edf5", borderRadius: 11, overflow: "hidden", marginBottom: 16 }}>
                            <AttestationTemplate data={previewAtt} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                            <Btn onClick={() => window.print()} variant="ghost" icon={<Printer style={{ width: 14, height: 14 }} />}>Imprimer</Btn>
                            <Btn onClick={() => toast("success", "PDF bientôt disponible")} icon={<Download style={{ width: 14, height: 14 }} />}>Télécharger PDF</Btn>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default AttestationManager;